import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { AuthService, BaseSiteService, CurrencyService, SiteContext, SiteContextConfig } from '@spartacus/core';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, Observable, iif, of, zip } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { getContextParameterDefault, getContextParameterValues } from 'src/app/core/site-context/context-config-utils';
import { B2bUserService } from '../b2buser/b2b-user.service';
import { ShimanoBusinessTypeService } from '../businessType/shimano-business-type.service';
import { ComponentService } from '../component.service';
import { SiteContextService } from '../site-context/site-context.service';
import { StorageService } from '../storage/storage.service';

export const baseSitePrefix = 'shimanoBike-';

export function baseSiteToCountry(baseSite: string): string {
  if (baseSite.startsWith(baseSitePrefix)) {
    const country = baseSite.slice(baseSitePrefix.length).toLowerCase();
    return country;
  }
}

export function countryToBaseSite(country: string): string {
  return baseSitePrefix + country.toLowerCase();
}

@Injectable({
  providedIn: 'root',
})
export class ShimanoCountryService implements SiteContext<string> {
  private activeCountry: BehaviorSubject<string>;

  constructor(
    private baseSiteService: BaseSiteService,
    private currencyService: CurrencyService,
    private componentService: ComponentService,
    private b2bUserService: B2bUserService,
    private authService: AuthService,
    private config: SiteContextConfig,
    private cookieService: CookieService,
    private shimanoBusinessType: ShimanoBusinessTypeService,
    private siteContextService: SiteContextService,
    private location: Location,
    private storageService: StorageService
  ) {
    this.activeCountry = new BehaviorSubject<string>(this.initCountry);
  }

  getAll(): Observable<string[]> {
    return of(getContextParameterValues(this.config, 'shimanoCountry'));
  }

  getActive(): Observable<string> {
    return this.activeCountry;
  }

  setActive(country: string) {
    this.authService
      .isUserLoggedIn()
      .pipe(
        switchMap((isUserLoggedIn) => iif(() => isUserLoggedIn, this.b2bUserService.getB2bUserProfile(), of(null))),
        switchMap((user) => {
          let userCountry: string;
          if (user && user.uid && user.currency) {
            userCountry = user.uid.split(':')[1];
            this.currencyService.setActive(user.currency.isocode);
            this.componentService.setActiveCurrency(user.currency.isocode);
            this.storageService.setSessionStorageItem('currency', user.currency.isocode);
          } else {
            userCountry = country;
          }
          this.activeCountry.next(userCountry);
          this.cookieService.put('userCountry', userCountry);
          return this.shimanoBusinessType.getActive();
        }),
        switchMap((businessType) =>
          zip(
            this.baseSiteService.getActive(),
            this.siteContextService.getBaseSiteId$(businessType, this.activeCountry.getValue())
          )
        ),
        tap(([currentBaseSite, nextBaseSite]) => {
          if (currentBaseSite !== nextBaseSite) {
            this.baseSiteService.setActive(nextBaseSite);
          }
        })
      )
      .subscribe();
  }

  get initCountry(): string {
    return getContextParameterDefault(this.config, 'shimanoCountry');
  }
}
